import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Calendar,
  PlayCircle,
  Activity,
  CheckCircle,
  XCircle,
  Filter,
  MapPin,
  Eye,
  Ban,
  ChevronDown,
  ChevronUp,
  RefreshCw
} from 'lucide-react';
import { User, Event } from '../types/models';
import { TalentVideo } from '../types/models/search';
import { userManagementService } from '../services/userManagementService';
import { videoVerificationService } from '../services/videoVerificationService';
import { eventsService } from '../services/eventsService';
import EnhancedSearchBar from './search/EnhancedSearchBar';
import SearchResultsDisplay from './search/SearchResultsDisplay';
import AdvancedFiltersPanel from './search/AdvancedFiltersPanel';
import { BulkSelectionProvider } from '../contexts/BulkSelectionContext';
import { useAuth } from '../contexts/AuthContext';
import { SearchQuery, SearchResults, SearchFilters } from '../types/models/search';
import { useDashboardRealtimeStats } from '../hooks/useRealtimeSubscription';
import {
  eventMatchesAssignedRegions,
  getAssignedRegions,
  isRegionScopedAdmin,
  userMatchesAssignedRegions
} from '../utils/regionAccess';

interface DashboardStats {
  users: {
    total: number;
    active: number;
    suspended: number;
    verified: number;
    athletes: number;
    coaches: number;
    organizations: number;
  };
  videos: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
  };
  events: {
    total: number;
    active: number;
  };
}

const getUserInitial = (user: User) => {
  const primaryText = user.displayName?.trim() || user.email?.trim() || 'U';
  return primaryText.charAt(0).toUpperCase();
};

const UserAvatar: React.FC<{
  user: User;
  sizeClass: string;
  textClass: string;
}> = ({ user, sizeClass, textClass }) => {
  const [imageError, setImageError] = useState(false);
  const showImage = Boolean(user.photoURL) && !imageError;
  const labelText = user.displayName?.trim() || user.email?.trim() || 'User';

  if (showImage) {
    return (
      <img
        src={user.photoURL as string}
        alt={labelText}
        onError={() => setImageError(true)}
        className={`${sizeClass} rounded-full object-cover`}
      />
    );
  }

  return (
    <div
      className={`${sizeClass} rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold select-none ${textClass}`}
      title={labelText}
    >
      {getUserInitial(user)}
    </div>
  );
};

const Dashboard: React.FC = () => {
  const { adminProfile } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUserRaw, setSelectedUserRaw] = useState<Record<string, any> | null>(null);
  const [isUserDetailLoading, setIsUserDetailLoading] = useState(false);
  
  // Enhanced search states
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [currentFilters, setCurrentFilters] = useState<SearchFilters>({});
  const [useEnhancedSearch, setUseEnhancedSearch] = useState(false);
  
  // Legacy filter states (for backward compatibility)
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'athlete' | 'coach' | 'organization'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'suspended' | 'verified'>('all');
  const [locationFilter, setLocationFilter] = useState('');
  const [sportFilter, setSportFilter] = useState('');
  const [genderFilter, setGenderFilter] = useState<'all' | 'male' | 'female' | 'other'>('all');
  const [ageFilter, setAgeFilter] = useState('');
  const [ageOperator, setAgeOperator] = useState<'exact' | 'greater' | 'less'>('exact');
  const [achievementFilter, setAchievementFilter] = useState('');

  // Debounce ref for real-time updates
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isLive, setIsLive] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const assignedRegions = getAssignedRegions(adminProfile);
  const isRegionScoped = isRegionScopedAdmin(adminProfile);

  const loadDashboardData = useCallback(async () => {
    try {
      console.log('🚀 Starting to load dashboard data...');
      setLoading(true);
      setUsersLoading(true);

      console.log('📍 Calling getUserStats...');
      const userStats = await userManagementService.getUserStats();
      console.log('✅ User stats loaded:', userStats);

      console.log('📍 Calling getVerificationStats...');
      const videoStats = await videoVerificationService.getVerificationStats();
      const allVideos = await videoVerificationService.getAllVideos();
      const scopedVideos = allVideos.filter((video) =>
        userMatchesAssignedRegions(adminProfile, {
          state: video.userState,
          location: video.userLocation,
          city: video.userCity
        } as User)
      );
      const scopedVideoStats = {
        total: scopedVideos.length,
        pending: scopedVideos.filter((video) => video.verificationStatus === 'pending').length,
        approved: scopedVideos.filter((video) => video.verificationStatus === 'approved').length,
        rejected: scopedVideos.filter((video) => video.verificationStatus === 'rejected').length
      };
      console.log('✅ Video stats loaded:', videoStats);

      console.log('📍 Calling getAllEvents...');
      const events = await eventsService.getAllEvents();
      console.log('✅ Events loaded:', events.length, 'events');

      console.log('📍 Calling getAllUsers...');
      const allUsers = await userManagementService.getAllUsers();
      const scopedUsers = allUsers.filter(user => userMatchesAssignedRegions(adminProfile, user));
      const scopedUserStats = {
        total: scopedUsers.length,
        active: scopedUsers.filter((user) => user.isActive).length,
        suspended: scopedUsers.filter((user) => user.isActive === false).length,
        verified: scopedUsers.filter((user) => user.isVerified).length,
        athletes: scopedUsers.filter((user) => user.role === 'athlete').length,
        coaches: scopedUsers.filter((user) => user.role === 'coach').length,
        organizations: scopedUsers.filter((user) => user.role === 'organization').length
      };
      console.log('✅ Users loaded:', allUsers.length, 'users');

      const scopedEvents = events.filter((event) => eventMatchesAssignedRegions(adminProfile, event));
      const eventStats = {
        total: scopedEvents.length,
        active: scopedEvents.filter(e => e.isActive).length
      };

      setStats({
        users: isRegionScoped ? scopedUserStats : userStats,
        videos: isRegionScoped ? scopedVideoStats : videoStats,
        events: eventStats
      });

      setUsers(scopedUsers);
      console.log('✅ Dashboard data loaded successfully');
    } catch (error) {
      console.error('❌ Error loading dashboard data:', error);
      console.error('Error details:', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setLoading(false);
      setUsersLoading(false);
    }
  }, [adminProfile, isRegionScoped]);

  // Debounced refresh function for real-time updates
  const debouncedRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    refreshTimeoutRef.current = setTimeout(() => {
      console.log('[Dashboard] Real-time update detected, refreshing data...');
      loadDashboardData();
      setLastUpdate(new Date());
    }, 1000); // 1 second debounce
  }, [loadDashboardData]);

  // Subscribe to real-time changes from Supabase
  useDashboardRealtimeStats(debouncedRefresh, isLive);

  useEffect(() => {
    loadDashboardData();
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [loadDashboardData]);

  const parseDateValue = (value: unknown): Date | null => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    if (typeof value === 'object' && value !== null && 'toDate' in value) {
      try {
        const parsed = (value as { toDate: () => Date }).toDate();
        return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed : null;
      } catch {
        return null;
      }
    }
    return null;
  };

  const formatUserDate = (value?: User['createdAt'] | User['updatedAt'] | User['lastSeen']) => {
    const normalizedDate = parseDateValue(value);
    return normalizedDate ? normalizedDate.toLocaleDateString() : '';
  };

  const formatDetailValue = (value: unknown, fallback = 'Not provided') => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string') {
      const normalized = value.trim();
      return normalized || fallback;
    }
    if (Array.isArray(value)) {
      const normalized = value
        .map((entry) => (entry === null || entry === undefined ? '' : String(entry).trim()))
        .filter(Boolean);
      return normalized.length ? normalized.join(', ') : fallback;
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return String(value);
  };

  const getItemDateByField = (
    item: User | TalentVideo | Event,
    field: 'createdAt' | 'updatedAt' | 'lastLoginAt'
  ): Date | null => {
    if (field === 'createdAt') return parseDateValue(item.createdAt);
    if (field === 'updatedAt') return parseDateValue((item as any).updatedAt);
    if (field === 'lastLoginAt') return parseDateValue((item as any).lastLoginAt);
    return null;
  };

  const matchesDateRange = (
    item: User | TalentVideo | Event,
    dateRange?: SearchFilters['dateRange']
  ): boolean => {
    if (!dateRange) return true;
    const itemDate = getItemDateByField(item, dateRange.field);
    if (!itemDate) return false;
    return itemDate >= dateRange.start && itemDate <= dateRange.end;
  };

  const includesSearchTerm = (term: string, values: Array<string | undefined | null>): boolean => {
    if (!term) return true;
    const normalizedTerm = term.toLowerCase();
    return values.some((value) => (value || '').toLowerCase().includes(normalizedTerm));
  };

  const buildUserFacets = (matchedUsers: User[]) => {
    const roles: Record<string, number> = {};
    const statuses: Record<string, number> = {};
    const locations: Record<string, number> = {};

    matchedUsers.forEach((user) => {
      const role = user.role || 'unknown';
      roles[role] = (roles[role] || 0) + 1;

      const status = user.isActive ? 'active' : 'suspended';
      statuses[status] = (statuses[status] || 0) + 1;
      if (user.isVerified) {
        statuses.verified = (statuses.verified || 0) + 1;
      }

      if (user.location) {
        locations[user.location] = (locations[user.location] || 0) + 1;
      }
    });

    return { roles, statuses, locations };
  };

  const buildVideoFacets = (matchedVideos: TalentVideo[]) => {
    const verificationStatuses: Record<string, number> = {};
    const categories: Record<string, number> = {};

    matchedVideos.forEach((video) => {
      verificationStatuses[video.verificationStatus] = (verificationStatuses[video.verificationStatus] || 0) + 1;
      if (video.category) {
        categories[video.category] = (categories[video.category] || 0) + 1;
      }
    });

    return { verificationStatuses, categories };
  };

  const buildEventFacets = (matchedEvents: Event[]) => {
    const eventStatuses: Record<string, number> = {};
    matchedEvents.forEach((event) => {
      const activeStatus = event.isActive ? 'active' : 'inactive';
      eventStatuses[activeStatus] = (eventStatuses[activeStatus] || 0) + 1;
      if (event.status === 'completed') {
        eventStatuses.completed = (eventStatuses.completed || 0) + 1;
      }
    });
    return { eventStatuses };
  };

  // Enhanced search handlers
  const handleEnhancedSearch = async (query: SearchQuery) => {
    setIsSearching(true);
    setSearchError(null);
    setUseEnhancedSearch(true);

    try {
      const searchTermNormalized = query.term.trim().toLowerCase();
      const activeFilters = query.filters || {};
      const limit = query.limit || 20;
      const hasUserSpecificFilters = Boolean(
        activeFilters.role?.length ||
        activeFilters.status?.length ||
        activeFilters.ageRange
      );
      const hasVideoSpecificFilters = Boolean(
        activeFilters.verificationStatus?.length ||
        activeFilters.category?.length
      );
      const hasEventSpecificFilters = Boolean(activeFilters.eventStatus?.length);
      const defaultToAllTypes = !hasUserSpecificFilters && !hasVideoSpecificFilters && !hasEventSpecificFilters;
      const shouldSearchUsers =
        query.searchType === 'users' ||
        (query.searchType === 'all' && (defaultToAllTypes || hasUserSpecificFilters));
      const shouldSearchVideos =
        query.searchType === 'videos' ||
        (query.searchType === 'all' && (defaultToAllTypes || hasVideoSpecificFilters));
      const shouldSearchEvents =
        query.searchType === 'events' ||
        (query.searchType === 'all' && (defaultToAllTypes || hasEventSpecificFilters));

      const matchedUsers: User[] = [];
      const matchedVideos: TalentVideo[] = [];
      const matchedEvents: Event[] = [];

      if (shouldSearchUsers) {
        const allUsers = await userManagementService.getAllUsers();
        const scopedUsers = allUsers.filter((user) => userMatchesAssignedRegions(adminProfile, user));

        scopedUsers.forEach((user) => {
          if (!matchesDateRange(user, activeFilters.dateRange)) return;
          if (activeFilters.role?.length && (!user.role || !activeFilters.role.includes(user.role as any))) return;
          if (activeFilters.status?.length) {
            const matchesStatus = activeFilters.status.some((status) => {
              if (status === 'active') return user.isActive;
              if (status === 'suspended') return !user.isActive;
              if (status === 'verified') return user.isVerified;
              return false;
            });
            if (!matchesStatus) return;
          }
          if (activeFilters.location && !(user.location || '').toLowerCase().includes(activeFilters.location.toLowerCase())) return;
          if (activeFilters.sport && !((user.sports || []).some((sport) => sport.toLowerCase().includes(activeFilters.sport!.toLowerCase())))) return;
          if (activeFilters.ageRange && user.dateOfBirth) {
            const birthDate = new Date(user.dateOfBirth);
            if (!Number.isNaN(birthDate.getTime())) {
              const now = new Date();
              let age = now.getFullYear() - birthDate.getFullYear();
              const monthDiff = now.getMonth() - birthDate.getMonth();
              if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birthDate.getDate())) {
                age--;
              }
              if (age < activeFilters.ageRange.min || age > activeFilters.ageRange.max) return;
            }
          }

          if (!includesSearchTerm(searchTermNormalized, [
            user.displayName,
            user.email,
            user.username,
            user.bio,
            user.location,
            ...(user.sports || [])
          ])) {
            return;
          }

          matchedUsers.push(user);
        });
      }

      if (shouldSearchVideos) {
        const allVideos = await videoVerificationService.getAllVideos();
        const scopedVideos = allVideos.filter((video) =>
          userMatchesAssignedRegions(adminProfile, {
            state: video.userState,
            location: video.userLocation,
            city: video.userCity
          } as User)
        );

        scopedVideos.forEach((video) => {
          if (!matchesDateRange(video, activeFilters.dateRange)) return;
          if (activeFilters.verificationStatus?.length && !activeFilters.verificationStatus.includes(video.verificationStatus)) return;
          if (
            activeFilters.category?.length &&
            (!video.category || !activeFilters.category.some((category) => video.category!.toLowerCase().includes(category.toLowerCase())))
          ) return;
          if (activeFilters.location && !(`${video.userLocation || ''} ${video.userCity || ''} ${video.userState || ''}`.toLowerCase().includes(activeFilters.location.toLowerCase()))) return;
          if (activeFilters.sport && !(video.category || '').toLowerCase().includes(activeFilters.sport.toLowerCase())) return;

          if (!includesSearchTerm(searchTermNormalized, [
            video.title,
            video.description,
            video.userName,
            video.userId,
            video.userLocation,
            video.userCity,
            video.userState,
            video.category
          ])) {
            return;
          }

          matchedVideos.push(video);
        });
      }

      if (shouldSearchEvents) {
        const allEvents = await eventsService.getAllEvents();
        const scopedEvents = allEvents.filter((event) => eventMatchesAssignedRegions(adminProfile, event));

        scopedEvents.forEach((event) => {
          if (!matchesDateRange(event, activeFilters.dateRange)) return;
          if (activeFilters.eventStatus?.length) {
            const matchesEventStatus = activeFilters.eventStatus.some((status) => {
              if (status === 'active') return event.isActive;
              if (status === 'inactive') return !event.isActive;
              if (status === 'completed') return event.status === 'completed';
              return false;
            });
            if (!matchesEventStatus) return;
          }
          if (activeFilters.location && !(event.location || '').toLowerCase().includes(activeFilters.location.toLowerCase())) return;
          if (activeFilters.sport && !(event.category || '').toLowerCase().includes(activeFilters.sport.toLowerCase())) return;

          if (!includesSearchTerm(searchTermNormalized, [
            event.title,
            event.description,
            event.location,
            event.category,
            event.organizer
          ])) {
            return;
          }

          matchedEvents.push(event);
        });
      }

      const combinedItems = [...matchedUsers, ...matchedVideos, ...matchedEvents].sort((a, b) => {
        const left = parseDateValue(a.createdAt)?.getTime() || 0;
        const right = parseDateValue(b.createdAt)?.getTime() || 0;
        return right - left;
      });

      const limitedItems = combinedItems.slice(0, limit);
      const relevanceScores = limitedItems.reduce<Record<string, number>>((scores, item, index) => {
        scores[item.id] = Math.max(0.1, 1 - (index / Math.max(1, limitedItems.length)));
        return scores;
      }, {});

      setSearchResults({
        items: limitedItems,
        totalCount: combinedItems.length,
        searchTime: 0,
        facets: {
          ...buildUserFacets(matchedUsers),
          ...buildVideoFacets(matchedVideos),
          ...buildEventFacets(matchedEvents)
        },
        relevanceScores,
        query
      });
    } catch (error) {
      console.error('Enhanced search error:', error);
      setSearchError('An unexpected error occurred during search');
      setSearchResults(null);
    } finally {
      setIsSearching(false);
    }
  };

  const handleFiltersChange = (filters: SearchFilters) => {
    setCurrentFilters(filters);

    const baseQuery = searchResults?.query;
    const term = baseQuery?.term || '';
    const searchType = baseQuery?.searchType || 'all';
    const hasFilters = Object.keys(filters).length > 0;
    const shouldRunSearch = hasFilters || Boolean(term.trim()) || useEnhancedSearch;

    if (shouldRunSearch) {
      const updatedQuery: SearchQuery = {
        term,
        searchType,
        filters,
        limit: baseQuery?.limit || 20,
        fuzzyMatching: true
      };
      handleEnhancedSearch(updatedQuery);
    } else {
      setUseEnhancedSearch(false);
      setSearchResults(null);
      setSearchError(null);
    }
  };

  const openUserDetails = useCallback(async (user: User) => {
    setSelectedUser(user);
    setSelectedUserRaw(null);
    setShowUserModal(true);
    setIsUserDetailLoading(true);

    try {
      const [detailedUser, rawUser] = await Promise.all([
        userManagementService.getUserById(user.id),
        userManagementService.getUserRawById(user.id)
      ]);

      if (detailedUser) {
        setSelectedUser(detailedUser);
      }
      if (rawUser) {
        setSelectedUserRaw(rawUser);
      }
    } catch (error) {
      console.error('Failed to load detailed user data:', error);
    } finally {
      setIsUserDetailLoading(false);
    }
  }, []);

  const handleItemClick = (item: User | TalentVideo | Event) => {
    if ('role' in item) {
      openUserDetails(item as User);
    }
    // Handle other item types as needed
  };

  const handleFacetClick = (facetType: string, facetValue: string) => {
    // Update filters based on facet selection
    const newFilters = { ...currentFilters };
    
    switch (facetType) {
      case 'roles':
        newFilters.role = newFilters.role || [];
        if (!newFilters.role.includes(facetValue as any)) {
          newFilters.role.push(facetValue as any);
        }
        break;
      case 'statuses':
        const normalizedStatus = facetValue === 'inactive' ? 'suspended' : facetValue;
        newFilters.status = newFilters.status || [];
        if (!newFilters.status.includes(normalizedStatus as any)) {
          newFilters.status.push(normalizedStatus as any);
        }
        break;
      case 'locations':
        newFilters.location = facetValue;
        break;
      // Add more facet types as needed
    }
    
    handleFiltersChange(newFilters);
  };

  const handleClearSearch = () => {
    setUseEnhancedSearch(false);
    setSearchResults(null);
    setSearchError(null);
    setCurrentFilters({});
  };

  const handleShowAllSearchResults = () => {
    if (!searchResults) return;
    const expandedLimit = Math.max(searchResults.totalCount, searchResults.items.length, 20);
    handleEnhancedSearch({
      ...searchResults.query,
      limit: expandedLimit
    });
  };

  // Filter users based on all criteria
  const filteredUsers = users.filter(user => {
    // Text search
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      const sportsStr = user.sports?.join(' ').toLowerCase() || '';
      const matchesSearch =
        user.displayName?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.bio?.toLowerCase().includes(searchLower) ||
        user.location?.toLowerCase().includes(searchLower) ||
        sportsStr.includes(searchLower);

      if (!matchesSearch) return false;
    }

    // Role filter
    if (roleFilter !== 'all' && user.role !== roleFilter) return false;

    // Status filter
    if (statusFilter === 'active' && (!user.isActive)) return false;
    if (statusFilter === 'suspended' && user.isActive) return false;
    if (statusFilter === 'verified' && !user.isVerified) return false;

    // Location filter
    if (locationFilter.trim() && user.location &&
        !user.location.toLowerCase().includes(locationFilter.toLowerCase())) return false;

    // Sport filter
    if (sportFilter.trim() && user.sports && user.sports.length > 0) {
      const hasSport = user.sports.some(sport =>
        sport.toLowerCase().includes(sportFilter.toLowerCase())
      );
      if (!hasSport) return false;
    } else if (sportFilter.trim()) {
      return false;
    }

    // Gender filter
    if (genderFilter !== 'all' && user.gender !== genderFilter) return false;

    // Age filter - calculate from dateOfBirth if available
    if (ageFilter.trim() && user.dateOfBirth) {
      const filterAge = parseInt(ageFilter);
      if (!isNaN(filterAge)) {
        const birthDate = new Date(user.dateOfBirth);
        const today = new Date();
        let userAge = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          userAge--;
        }
        if (ageOperator === 'exact' && userAge !== filterAge) return false;
        if (ageOperator === 'greater' && userAge <= filterAge) return false;
        if (ageOperator === 'less' && userAge >= filterAge) return false;
      }
    }

    return true;
  });

  const clearAllFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
    setStatusFilter('all');
    setLocationFilter('');
    setSportFilter('');
    setGenderFilter('all');
    setAgeFilter('');
    setAchievementFilter('');
    setAgeOperator('exact');
  };


  const StatCard: React.FC<{
    title: string;
    value?: number;
    subtitle?: string;
    icon: React.ElementType;
    color: string;
    link: string;
  }> = ({ title, value = 0, subtitle, icon: Icon, color, link }) => (
    <Link
      to={link}
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-3xl font-bold text-gray-900">{typeof value === 'number' ? value.toLocaleString() : '0'}</p>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </Link>
  );

  const UserCard: React.FC<{ user: User }> = ({ user }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <img
            src={user.photoURL || 'https://via.placeholder.com/48'}
            alt={user.displayName}
            className="w-12 h-12 rounded-full object-cover"
          />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{user.displayName}</h3>
            <p className="text-sm text-gray-600">{user.email}</p>
          </div>
        </div>
        <button
          onClick={() => {
            openUserDetails(user);
          }}
          className="text-gray-400 hover:text-gray-600 p-1"
        >
          <Eye className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            user.role === 'athlete' ? 'bg-blue-100 text-blue-800' :
            user.role === 'coach' ? 'bg-green-100 text-green-800' :
            'bg-purple-100 text-purple-800'
          }`}>
            {user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'User'}
          </span>

          {user.isVerified && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <CheckCircle className="w-3 h-3 mr-1" />
              Verified
            </span>
          )}

          {!user.isActive && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
              <Ban className="w-3 h-3 mr-1" />
              Inactive
            </span>
          )}
        </div>

        {user.location && (
          <div className="flex items-center text-sm text-gray-600">
            <MapPin className="w-4 h-4 mr-2" />
            {user.location}
          </div>
        )}

        {user.sports && Array.isArray(user.sports) && user.sports.length > 0 && (
          <div className="flex items-center text-sm text-gray-600">
            <Activity className="w-4 h-4 mr-2" />
            {user.sports.join(', ')}
          </div>
        )}

        <div className="flex items-center justify-between text-sm text-gray-500 mt-3">
          <span>{user.followersCount || 0} followers</span>
          <span>{user.postsCount || 0} posts</span>
        </div>
      </div>
    </div>
  );

  const UserModal: React.FC<{
    user: User;
    rawUser: Record<string, any> | null;
    isLoadingDetails: boolean;
    onClose: () => void;
  }> = ({ user, rawUser, isLoadingDetails, onClose }) => {
    const positionValue =
      user.positionName && user.position && user.positionName !== user.position
        ? `${user.positionName} (${user.position})`
        : user.positionName || user.position;
    const subcategoryValue =
      user.subcategoryName && user.subcategory && user.subcategoryName !== user.subcategory
        ? `${user.subcategoryName} (${user.subcategory})`
        : user.subcategoryName || user.subcategory;
    const sportDetailsValue = (user.sportDetails || [])
      .map((sport) => sport?.name || sport?.id)
      .filter(Boolean)
      .join(', ');

    return (
      <div className="fixed inset-0 z-50 bg-gray-600 bg-opacity-50 overflow-y-auto p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-auto max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-xl font-bold text-gray-900">User Details</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <UserAvatar user={user} sizeClass="w-20 h-20" textClass="text-2xl" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{formatDetailValue(user.displayName, 'Unnamed User')}</h3>
                  <p className="text-gray-600">{formatDetailValue(user.email)}</p>
                  <p className="text-sm text-gray-600 mt-1">Mobile: {formatDetailValue(user.mobile || user.phone)}</p>
                  <div className="flex items-center space-x-2 mt-2">
                    {user.isVerified && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Verified
                      </span>
                    )}
                    {!user.isActive && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <Ban className="w-3 h-3 mr-1" />
                        Inactive
                      </span>
                    )}
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      user.role === 'athlete' ? 'bg-blue-100 text-blue-800' :
                      user.role === 'coach' ? 'bg-green-100 text-green-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>
                      {user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'User'}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Identity & Contact</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">User ID</label>
                    <p className="mt-1 text-sm text-gray-900 font-mono bg-gray-50 p-2 rounded border">{formatDetailValue(user.id)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">UID</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.uid)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Username</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.username)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="mt-1 text-sm text-gray-900 break-all">{formatDetailValue(user.email)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Mobile Number</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.mobile)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Alternate Phone</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.phone)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Website</label>
                    <p className="mt-1 text-sm text-gray-900 break-all">{formatDetailValue(user.website)}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Bio</label>
                    <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{formatDetailValue(user.bio)}</p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Location & Personal</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Location</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.location)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Country</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.country)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">State</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.state)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">City</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.city)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.dateOfBirth)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Gender</label>
                    <p className="mt-1 text-sm text-gray-900 capitalize">{formatDetailValue(user.gender)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Height</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.height)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Weight</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.weight)}</p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Sports Profile</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Sports</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.sports, 'Not specified')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Event Types</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.eventTypes, 'Not specified')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Position</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(positionValue, 'Not specified')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Subcategory</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(subcategoryValue, 'Not specified')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Specializations</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.specializations, 'Not specified')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Sport Details</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(sportDetailsValue, 'Not specified')}</p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Account</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Account Status</label>
                    <p className="mt-1 text-sm text-gray-900">{user.isActive ? 'Active' : 'Inactive'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Profile Status</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.status, 'Not set')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Online</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.isOnline, 'Unknown')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Joined</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(formatUserDate(user.createdAt), 'N/A')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Updated</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(formatUserDate(user.updatedAt), 'N/A')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Seen</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(formatUserDate(user.lastSeen), 'Not available')}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Suspension Reason</label>
                    <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{formatDetailValue(user.suspension_reason, 'None')}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{user.followersCount || 0}</p>
                  <p className="text-sm text-gray-600">Followers</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{user.followingCount || 0}</p>
                  <p className="text-sm text-gray-600">Following</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{user.postsCount || 0}</p>
                  <p className="text-sm text-gray-600">Posts</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{user.storiesCount || 0}</p>
                  <p className="text-sm text-gray-600">Stories</p>
                </div>
              </div>

              {(user.athleteProfile || user.privacy || user.settings) && (
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900">Additional Raw Details</h4>
                  {user.athleteProfile && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Athlete Profile JSON</label>
                      <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded-lg overflow-auto max-h-64">
                        {JSON.stringify(user.athleteProfile, null, 2)}
                      </pre>
                    </div>
                  )}
                  {user.privacy && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Privacy JSON</label>
                      <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded-lg overflow-auto max-h-64">
                        {JSON.stringify(user.privacy, null, 2)}
                      </pre>
                    </div>
                  )}
                  {user.settings && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Settings JSON</label>
                      <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded-lg overflow-auto max-h-64">
                        {JSON.stringify(user.settings, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}

              {isLoadingDetails && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                  Loading latest user details from database...
                </div>
              )}

              {rawUser && (
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900">Database Row (All Fields)</h4>
                  <div className="rounded-lg border border-gray-200 divide-y divide-gray-200">
                    {Object.entries(rawUser).sort(([left], [right]) => left.localeCompare(right)).map(([key, value]) => (
                      <div key={key} className="px-4 py-3">
                        <p className="text-xs uppercase tracking-wide text-gray-500">{key}</p>
                        {value !== null && typeof value === 'object' ? (
                          <pre className="mt-1 text-xs bg-gray-900 text-gray-100 p-3 rounded-lg overflow-auto max-h-56">
                            {JSON.stringify(value, null, 2)}
                          </pre>
                        ) : (
                          <p className="mt-1 text-sm text-gray-900 break-all">{formatDetailValue(value, 'null')}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600 mt-1">Welcome to the AmaPlayer admin dashboard</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* Live Status Indicator */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsLive(!isLive)}
                className={`flex items-center space-x-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  isLive
                    ? 'bg-green-100 text-green-800 hover:bg-green-200'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${isLive ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                <span>{isLive ? 'Live' : 'Paused'}</span>
              </button>
              {lastUpdate && (
                <span className="text-xs text-gray-500">
                  Updated: {lastUpdate.toLocaleTimeString()}
                </span>
              )}
            </div>
            {/* Manual Refresh Button */}
            <button
              onClick={() => {
                loadDashboardData();
                setLastUpdate(new Date());
              }}
              disabled={loading}
              className="flex items-center space-x-2 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
        {isRegionScoped && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Dashboard user views are limited to assigned regions:
            {' '}
            <span className="font-medium">
              {assignedRegions.length > 0 ? assignedRegions.join(', ') : 'No regions assigned'}
            </span>
          </div>
        )}
      </div>

      {/* Main Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Users"
            value={stats.users.total}
            subtitle={`${stats.users.active} active`}
            icon={Users}
            color="bg-blue-500"
            link="/users"
          />
          <StatCard
            title="Events"
            value={stats.events.total}
            subtitle={`${stats.events.active} active`}
            icon={Calendar}
            color="bg-green-500"
            link="/events"
          />
          <StatCard
            title="Videos"
            value={stats.videos.total}
            subtitle={`${stats.videos.pending} pending review`}
            icon={PlayCircle}
            color="bg-purple-500"
            link="/videos"
          />
          <StatCard
            title="Verified Users"
            value={stats.users.verified}
            subtitle={`${Math.round((stats.users.verified / stats.users.total) * 100)}% of total`}
            icon={CheckCircle}
            color="bg-indigo-500"
            link="/users"
          />
        </div>
      )}

      {/* Enhanced Search Section */}
      <BulkSelectionProvider>
        <div className="space-y-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <h2 className="text-xl font-bold text-gray-900">
              {useEnhancedSearch ? 'Search Results' : 'All Users'}
            </h2>
            <div className="flex items-center gap-3">
              {useEnhancedSearch ? (
                <>
                  <button
                    onClick={handleClearSearch}
                    className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 text-sm font-medium"
                  >
                    Back to All Users
                  </button>
                  {searchResults && searchResults.items.length < searchResults.totalCount && (
                    <button
                      onClick={handleShowAllSearchResults}
                      className="px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm font-medium"
                    >
                      Show All Results ({searchResults.totalCount})
                    </button>
                  )}
                </>
              ) : (
                <>
                  <span className="text-sm text-gray-600">
                    Showing {filteredUsers.length} of {users.length} users
                  </span>
                  <Link
                    to="/users"
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    Advanced Management
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Enhanced Search Bar */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <EnhancedSearchBar
              onSearch={handleEnhancedSearch}
              placeholder="Search users, videos, events..."
              enableAutoComplete={true}
              enableSavedSearches={true}
              searchTypes={['all', 'users', 'videos', 'events']}
              initialFilters={currentFilters}
              className="mb-4"
            />

            {/* Advanced Filters Toggle */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200"
              >
                <Filter className="w-4 h-4" />
                <span>Advanced Filters</span>
                {showAdvancedFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>

              {!useEnhancedSearch && (
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors duration-200"
                >
                  <span>Legacy Filters</span>
                  {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
              )}
            </div>

            {/* Advanced Filters Panel */}
            {showAdvancedFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <AdvancedFiltersPanel
                  filters={currentFilters}
                  onFiltersChange={handleFiltersChange}
                  searchType="all"
                />
              </div>
            )}

            {/* Legacy Filters (for backward compatibility) */}
            {!useEnhancedSearch && showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                  <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value as any)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Roles</option>
                    <option value="athlete">Athletes</option>
                    <option value="coach">Coaches</option>
                    <option value="organization">Organizations</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="verified">Verified</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                  <input
                    type="text"
                    placeholder="Filter by location..."
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sport</label>
                  <input
                    type="text"
                    placeholder="Filter by sport..."
                    value={sportFilter}
                    onChange={(e) => setSportFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                  <select
                    value={genderFilter}
                    onChange={(e) => setGenderFilter(e.target.value as any)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Genders</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Age</label>
                  <div className="flex space-x-2">
                    <select
                      value={ageOperator}
                      onChange={(e) => setAgeOperator(e.target.value as any)}
                      className="w-20 px-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="exact">=</option>
                      <option value="greater">&gt;</option>
                      <option value="less">&lt;</option>
                    </select>
                    <input
                      type="number"
                      placeholder="Age"
                      value={ageFilter}
                      onChange={(e) => setAgeFilter(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Achievements</label>
                  <input
                    type="text"
                    placeholder="Filter by achievements..."
                    value={achievementFilter}
                    onChange={(e) => setAchievementFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="col-span-full">
                  <button
                    onClick={clearAllFilters}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
                  >
                    Clear All Filters
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Search Error Display */}
          {searchError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <XCircle className="w-5 h-5 text-red-400 mr-2" />
                <span className="text-red-800">{searchError}</span>
              </div>
            </div>
          )}

          {/* Search Loading */}
          {isSearching && (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
              <span className="ml-3 text-gray-600">Searching...</span>
            </div>
          )}

          {/* Enhanced Search Results */}
          {useEnhancedSearch && searchResults && !isSearching && (
            <SearchResultsDisplay
              results={searchResults}
              searchTerm={searchResults.query.term}
              onItemClick={handleItemClick}
              onFacetClick={handleFacetClick}
              enableBulkSelection={true}
              className="enhanced-search-results"
            />
          )}

          {/* Legacy Users Grid (when not using enhanced search) */}
          {!useEnhancedSearch && !isSearching && (
            <>
              {usersLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredUsers.slice(0, 12).map((user) => (
                      <UserCard key={user.id} user={user} />
                    ))}
                  </div>

                  {filteredUsers.length === 0 && (
                    <div className="text-center py-12">
                      <Users className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Try adjusting your search or filter criteria.
                      </p>
                    </div>
                  )}

                  {filteredUsers.length > 12 && (
                    <div className="text-center">
                      <Link
                        to="/users"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                      >
                        View All {filteredUsers.length} Users
                      </Link>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </BulkSelectionProvider>

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <UserModal
          user={selectedUser}
          rawUser={selectedUserRaw}
          isLoadingDetails={isUserDetailLoading}
          onClose={() => {
            setShowUserModal(false);
            setSelectedUser(null);
            setSelectedUserRaw(null);
            setIsUserDetailLoading(false);
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;
